/**
 * EDM 订阅入口埋点工具
 * 提供：IntersectionObserver 曝光检测、UUID 生成
 * 事件发送统一走 window.luteTrack()
 */
;(function () {

  const ss = {
    get: (key) => sessionStorage.getItem(key),
    set: (key, val) => { sessionStorage.setItem(key, val) }
  }

  function genId() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Date.now() + Math.random() * 16) % 16 | 0
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
    })
  }
  function getRequestId() {
    return `req_${genId()}`
  }

  function getEntryClickId() {
    return `clk_${genId()}`
  }

  /** 取最近一次来源点击 ID，用于串联来源点击与最终订阅目标页 */
  function getLastEntryClickId() {
    return ss.get('__edm_entry_click_id') || ''
  }

  function getSessionId() {
    let id = ss.get('__edm_session_id')
    if (!id) { id = `ses_${genId()}`; ss.set('__edm_session_id', id) }
    return id
  }

  /** 组件实例计数器：按前缀自增，保证同一组件下各实例 ID 唯一 */
  const instanceCounters = {}

  /**
   * 生成组件实例 ID（同一组件内唯一）
   * 优先用元素自身 id；无 id 时生成「前缀_序号」，并缓存在元素上保证多次调用一致
   * @param {Element} el
   * @param {string}  prefix 组件前缀，一般传 source_entry_id / subscription_entry_id
   * @returns {string}
   */
  function getInstanceId(el, prefix) {
    if (el.id) return el.id
    if (!el.__edmInstanceId) {
      instanceCounters[prefix] = (instanceCounters[prefix] || 0) + 1
      el.__edmInstanceId = `${prefix}_${instanceCounters[prefix]}`
    }
    return el.__edmInstanceId
  }

  const SOURCE_PAGE_CONTEXT = {
    web_register_entry: { page_type: 'member_center', page_id: 'register_popup' },
    member_hub_join_entry: { page_type: 'member_center', page_id: 'membership_join' },
    pdp_email_subscription: { page_type: 'product_detail', page_id: 'pdp_email_subscription' },
  }

  /** IntersectionObserver: 50% 可见 + 连续 1 秒 → callback 一次，同元素不重复 */
  function observeVisible(el, callback) {
    if (!el || !window.IntersectionObserver) return
    let timer = null
    let fired = false
    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.intersectionRatio >= 0.5) {
          if (!timer) {
            timer = setTimeout(function () {
              if (!fired) { fired = true; callback() }
              observer.disconnect()
            }, 1000)
          }
        } else {
          clearTimeout(timer); timer = null
        }
      })
    }, { threshold: 0.5 })
    observer.observe(el)
  }

  /**
   * 通用来源入口埋点初始化
   * 对页面上所有 [data-edm-source] 元素：
   *  - 曝光：IntersectionObserver 触发 email_subscribe_source_view
   *  - 点击：触发 email_subscribe_source_click
   * data-edm-source  = source_entry_id
   * data-edm-dest    = destination_subscription_entry_id（可选）
   */
  function initSourceEntries() {
    if (!window.luteTrack) return
    // 同一来源入口在同一 page_view 只报一次曝光
    const firedViews = window.__edmFiredViews || (window.__edmFiredViews = {})
    document.querySelectorAll('[data-edm-source]').forEach(function (el) {
      if (el.__edmBound) return
      el.__edmBound = true
      const sourceId = el.getAttribute('data-edm-source')
      const destId = el.getAttribute('data-edm-dest') || ''
      const productId = el.getAttribute('data-product-id') || ''
      const productCategory = el.getAttribute('data-product-category') || ''
      const pageContext = SOURCE_PAGE_CONTEXT[sourceId]
      // 曝光：同 source_entry_id 同 page_view 只报一次
      observeVisible(el, function () {
        if (firedViews[sourceId]) return
        firedViews[sourceId] = true
        const componentInstanceId = getInstanceId(el, sourceId)
        luteTrack('email_subscribe_source_view', {
          source_entry_id: sourceId,
          component_instance_id: componentInstanceId,
          destination_subscription_entry_id: destId,
          page_type: pageContext.page_type,
          page_id: pageContext.page_id,
          available_click_targets: el.tagName ? el.tagName.toLowerCase() : 'unknown',
          product_id: productId,
          product_category: productCategory,
        })
      })
      // 点击：capture 阶段先写 context，保证原脚本程序化跳转前 sessionStorage 已就绪
      el.addEventListener('click', function () {
        const entryClickId = getEntryClickId()
        ss.set('__edm_entry_click_id', entryClickId)
        luteTrack('email_subscribe_source_click', {
          source_entry_id: sourceId,
          entry_click_id: entryClickId,
          destination_url: el.getAttribute('href') || location.href,
          destination_subscription_entry_id: destId,
          component_instance_id: el.getAttribute('data-component-instance-id') || el.id || 'source_entry',
          page_type: pageContext.page_type,
          page_id: pageContext.page_id,
          product_id: productId,
          product_category: productCategory,
        })
      }, true)
    })
  }

  /**
   * Klaviyo 首页弹框实际订阅入口埋点（homepage_popup）
   * MutationObserver 监听 [data-testid="POPUP"] 动态注入（Klaviyo onsite popup）：
   *  - 弹框真实展示 → email_subscribe_view（曝光）
   *  - Claim My Free Guide 点击 → email_subscribe_click（提交点击）
   *  - 前端校验失败（邮箱为空/格式错误）→ email_subscribe_submit_fail
* 成功（email_subscribe_submit_success）由服务端上报，前端不上报。
 * 弹框在当前页展开，非跨页跳转；来源上下文不在此追踪（source_entry_id/entry_click_id 不再埋）。
 */
  function initKlaviyoPopup() {
    if (!window.luteTrack) return
    const SUB_ID = 'homepage_popup'
    const page_type = 'home'
    const page_id = 'homepage'
    let popupFired = false
    let requestId = ''

    function emailState(popup) {
      const el = popup.querySelector('input[type="email"][name="email"]')
      const v = (el && el.value || '').trim()
      return {
        email_input_state: v === '' ? 'empty' : (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? 'valid' : 'invalid'),
      }
    }

    function consentState(popup) {
      const cb = popup.querySelector('input[type="checkbox"][name^="Create_Account_Request"]')
      return { consent_state: cb && cb.checked ? 'checked' : 'unchecked' }
    }

    const observer = new MutationObserver(function (mutations) {
      for (let i = 0; i < mutations.length; i++) {
        const nodes = mutations[i].addedNodes
        for (let j = 0; j < nodes.length; j++) {
          const node = nodes[j]
          if (node.nodeType !== 1) continue
          const popup = node.matches && node.matches('[data-testid="POPUP"]')
            ? node
            : node.querySelector && node.querySelector('[data-testid="POPUP"]')
          if (!popup) continue

          const componentInstanceId = getInstanceId(popup, SUB_ID)

          // 曝光：Popup 真实展示后，同 page_view 只报一次
          if (!popupFired) {
            popupFired = true
            luteTrack('email_subscribe_view', {
              subscription_entry_id: SUB_ID,
              component_type: 'popup',
              component_instance_id: componentInstanceId,
              default_checked: false,
              page_type,
              page_id,
            })
          }

          // 提交点击
          const submitBtn = popup.querySelector('button.klaviyo-form-button')
          if (submitBtn && !submitBtn.__edmBound) {
            submitBtn.__edmBound = true
            submitBtn.addEventListener('click', function () {
              const email = emailState(popup)
              requestId = requestId || getRequestId()
              // 前端校验失败：邮箱为空或格式错误
              if (email.email_input_state !== 'valid') {
                luteTrack('email_subscribe_submit_fail', {
                  subscription_entry_id: SUB_ID,
                  component_type: 'popup',
                  component_instance_id: componentInstanceId,
                  page_type,
                  page_id,
                  failure_stage: 'frontend_validation',
                  error_code: email.email_input_state,
                  error_type: 'validation',
                  retryable: true,
                  sync_result: 'failed',
                  request_id: requestId,
                })
              }
              // 点击代表发起操作，不代表订阅成功（成功由服务端报 submit_success）
              luteTrack('email_subscribe_click', {
                subscription_entry_id: SUB_ID,
                subscribe_source: 'homepage_popup_submit_button',
                component_type: 'popup',
                component_instance_id: componentInstanceId,
                page_type,
                page_id,
                email_input_state: email.email_input_state,
                consent_state: consentState(popup).consent_state,
                submit_action: 'subscribe',
                request_id: requestId,
              })
            })
          }
        }
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
  }

  /**
   * Footer newsletter 订阅表单埋点（site_footer）
   * defer 脚本执行时 DOM 已就绪，直接绑定
   * 选择器：.t4s-footer-wrap .t4s-newsletter-parent（footer.liquid section 内，footer_content 渲染）
   * 注意：.t4s-newsletter-parent 在 newslettter section / sidebar-blog / banner 等也出现，
   * 必须限定在 .t4s-footer-wrap 内，避免把全站 newsletter 表单都绑成 site_footer
   */
  function initFooterNewsletter() {
    if (!window.luteTrack) return
    const page_type = 'home'
    const page_id = 'footer_newsletter'

    document.querySelectorAll('.t4s-footer-wrap .t4s-newsletter-parent').forEach(function (wrap) {
      if (wrap.__edmBound) return
      wrap.__edmBound = true
      let requestId = ''

      const componentInstanceId = getInstanceId(wrap, 'site_footer')

      // 曝光：表单 50% 可见 + 连续 1 秒
      observeVisible(wrap, function () {
        luteTrack('email_subscribe_view', {
          subscription_entry_id: 'site_footer',
          component_type: 'form',
          component_instance_id: componentInstanceId,
          default_checked: false,
          page_type,
          page_id,
        })
      })

      // 点击 Subscribe 按钮
      const btn = wrap.querySelector('[data-agreemail-btn]') || wrap.querySelector('button[type="submit"]')
      if (!btn) return
      btn.addEventListener('click', function () {
        const emailEl = wrap.querySelector('input[type="email"]')
        const v = (emailEl && emailEl.value || '').trim()
        const emailState = !v ? 'empty' : (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? 'valid' : 'invalid')
        luteTrack('email_subscribe_click', {
          subscription_entry_id: 'site_footer',
          subscribe_source: 'site_footer_submit_button',
          component_type: 'form',
          component_instance_id: componentInstanceId,
          page_type,
          page_id,
          email_input_state: emailState,
          consent_state: 'checked',
          submit_action: 'subscribe',
          request_id: requestId || (requestId = getRequestId()),
        })
        if(emailState !== 'valid') {
          luteTrack('email_subscribe_submit_fail', {
            subscription_entry_id: 'site_footer',
            component_type: 'form',
            component_instance_id: componentInstanceId,
            page_type,
            page_id,
            request_id: requestId || (requestId = getRequestId()),
            failure_stage: 'frontend_validation',
            error_code: emailState === 'empty' ? 'empty' : 'invalid',
            error_type: 'checked',
            retryable: true,
            sync_result: 'failed',
            email_input_state: emailState,
          })
        }
      })
    })
  }

  /**
   * scene-v2 公共邮件订阅表单埋点（scene_v2_email_subcribe）
   * 容器：.scene-v2-email-subscribe（section 固定 class，20+ 页面共用）
   * 使用 <email-form> 自定义元素，结果反馈通过 .error-tips / .success-tips 展示
   * PC + Mobile 各渲染一次，分别绑定
   * subscription_entry_id 固定 scene_v2_email_subcribe（公共组件，不按页面区分）
   */
  function initPumpCollectionForm() {
    if (!window.luteTrack) return
    const SUB_ID = 'scene_v2_email_subcribe'
    const page_type = 'home'
    const page_id = 'newsletter_form'

    document.querySelectorAll('.scene-v2-email-subscribe').forEach(function (section, idx) {
      if (section.__edmBound) return
      section.__edmBound = true

      const componentInstanceId = getInstanceId(section, SUB_ID)
      let lastClickEmailState = 'empty'
      let requestId = ''
      // 曝光：section 50% 可见 + 连续 1 秒
      observeVisible(section, function () {
        luteTrack('email_subscribe_view', {
          subscription_entry_id: SUB_ID,
          component_type: 'form',
          component_instance_id: componentInstanceId,
          default_checked: false,
          page_type,
          page_id,
        })
      })

      // 点击提交按钮
      const btn = section.querySelector('button[type="button"]')
      if (btn) {
        btn.addEventListener('click', function () {
          const emailEl = section.querySelector('input[type="email"]')
          const v = (emailEl && emailEl.value || '').trim()
          lastClickEmailState = !v ? 'empty' : (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? 'valid' : 'invalid')
          luteTrack('email_subscribe_click', {
            subscription_entry_id: SUB_ID,
            subscribe_source: 'scene_v2_submit_button',
            component_type: 'form',
            component_instance_id: componentInstanceId,
            page_type,
            page_id,
            email_input_state: lastClickEmailState,
            consent_state: 'checked',
            submit_action: 'subscribe',
            request_id: requestId || (requestId = getRequestId()),
          })
        })
      }

      // 失败：监听 .error-tips 出现（email-form 校验失败/接口失败时插入）
      const emailForm = section.querySelector('email-form')
      if (emailForm) {
        new MutationObserver(function (mutations) {
          mutations.forEach(function (m) {
            m.addedNodes.forEach(function (node) {
              if (node.nodeType === 1 && node.classList && node.classList.contains('error-tips')) {
                luteTrack('email_subscribe_submit_fail', {
                  subscription_entry_id: SUB_ID,
                  component_type: 'form',
                  component_instance_id: componentInstanceId,
                  page_type,
                  page_id,
                  request_id: requestId,
                  failure_stage: 'frontend_validation',
                  error_code: node.textContent.trim(),
                  error_type: 'validation',
                  retryable: true,
                  sync_result: 'failed',
                  email_input_state: lastClickEmailState,
                })
              }
            })
          })
        }).observe(emailForm, { childList: true, subtree: true })
      }
    })
  }

  /**
   * 博客挑战页订阅表单埋点（challenges_form）
   * 页面：blogs/challenges（默认 blog.json 模板）
   * 表单来源：sidebar-blog 的 newsletter block，容器为 .t4s-sidebar-newsletter
   * 同一 block 渲染两份：桌面侧栏（.t4s-sidebar）与移动抽屉（.t4s-drawer__main）
   * footer 表单在 .t4s-footer-wrap 内，归 site_footer，天然不在本选择器内
   */
  function initChallengesForm() {
    if (!window.luteTrack) return
    const urlRef = location.pathname + location.search
    if (!/blogs\/challenges/.test(urlRef)) return
    const SUB_ID = 'challenges_form'
    const page_type = 'quiz'
    const page_id = 'challenges_newsletter'
    // sidebar 区块懒加载（template 克隆），表单延迟进入 DOM；等容器变化后再绑定
    const FORM_SEL = '.t4s-sidebar-newsletter form[data-form-mail-agree]'

    function bindForm(form, idx) {
      if (form.__edmBound) return
      form.__edmBound = true

      const wrap = form.closest('.t4s-newsletter-wrap') || form.closest('.t4s-sidebar-newsletter') || form
      const componentInstanceId = getInstanceId(wrap, SUB_ID)
      let lastClickEmailState = 'empty'
      let requestId = ''

      // 曝光：容器 50% 可见 + 连续 1 秒
      observeVisible(wrap, function () {
        luteTrack('email_subscribe_view', {
          subscription_entry_id: SUB_ID,
          component_type: 'form',
          component_instance_id: componentInstanceId,
          default_checked: false,
          page_type,
          page_id,
        })
      })

      // 点击提交按钮
      const btn = form.querySelector('button[type="submit"]')
      if (!btn) return
      btn.addEventListener('click', function () {
        const emailEl = form.querySelector('input[type="email"]')
        const v = (emailEl && emailEl.value || '').trim()
        lastClickEmailState = !v ? 'empty' : (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? 'valid' : 'invalid')
        luteTrack('email_subscribe_click', {
          subscription_entry_id: SUB_ID,
          subscribe_source: 'challenges_submit_button',
          component_type: 'form',
          component_instance_id: componentInstanceId,
          submit_action: 'subscribe',
          page_type,
          page_id,
          email_input_state: lastClickEmailState,
          consent_state: 'checked',
          request_id: requestId || (requestId = getRequestId()),
        })
      })

      // 失败：监听响应区 .t4s-newsletter__error 出现（Klaviyo ajax 失败时插入）
      const resp = form.querySelector('.klaviyo_messages, [data-new-response-form]')
      if (resp) {
        new MutationObserver(function (mutations) {
          mutations.forEach(function (m) {
            m.addedNodes.forEach(function (node) {
              if (node.nodeType === 1 && node.classList && node.classList.contains('t4s-newsletter__error')) {
                luteTrack('email_subscribe_submit_fail', {
                  subscription_entry_id: SUB_ID,
                  component_type: 'form',
                  component_instance_id: componentInstanceId,
                  page_type,
                  page_id,
                  request_id: requestId,
                  failure_stage: 'server_rejection',
                  error_code: node.textContent.trim(),
                  error_type: 'validation',
                  retryable: true,
                  sync_result: 'failed',
                  email_input_state: lastClickEmailState,
                })
              }
            })
          })
        }).observe(resp, { childList: true, subtree: true })
      }
    }

    // 首次尝试：DOM 已就绪常有表单；没有则等懒加载克隆（MutationObserver）
    const found = document.querySelectorAll(FORM_SEL)
    found.forEach(bindForm)
    if (!found.length) {
      const target = document.querySelector('.t4s-sidebar, .t4s-drawer__main') || document.body
      new MutationObserver(function () {
        const forms = document.querySelectorAll(FORM_SEL)
        if (forms.length) {
          forms.forEach(bindForm)
          this.disconnect()
        }
      }).observe(target, { childList: true, subtree: true })
    }
  }

  window.EdmTracker = {
    genId: genId,
    getSessionId: getSessionId,
    getEntryClickId: getEntryClickId,
    getLastEntryClickId: getLastEntryClickId,
    getInstanceId: getInstanceId,
    observeVisible: observeVisible,
    initSourceEntries: initSourceEntries,
    initKlaviyoPopup: initKlaviyoPopup,
    initFooterNewsletter: initFooterNewsletter,
    initPumpCollectionForm: initPumpCollectionForm,
    initChallengesForm: initChallengesForm,
  }

  /*
   * 自动初始化
   * window.luteTrack 不是本主题定义的，它来自 lute-track.liquid 动态注入的远程 theme-track.js，
   * 而后者要等 Shopify consent API 通过后才加载，必然晚于本 defer 脚本执行。
   * 因此不能直接初始化 —— 否则下面每个 init 都会卡在 `if (!window.luteTrack) return` 处静默退出。
   * 这里改为：等 luteTrack 就绪后再初始化，且带超时兜底 + 监听用户同意事件重试。
   */
  let edmBootstrapped = false
  function bootstrapEdm() {
    if (edmBootstrapped) return true
    if (typeof window.luteTrack !== 'function') return false
    edmBootstrapped = true
    initSourceEntries()
    initKlaviyoPopup()
    initFooterNewsletter()
    initPumpCollectionForm()
    initChallengesForm()
    return true
  }

  if (!bootstrapEdm()) {
    let waited = 0
    const timer = setInterval(function () {
      waited += 200
      if (bootstrapEdm() || waited >= 15000) clearInterval(timer)
    }, 200)
    // 用户同意 cookies 后主题才会加载 theme-track.js，这里再抓一次
    document.addEventListener('visitorConsentCollected', function () {
      if (bootstrapEdm()) clearInterval(timer)
    })
  }
})()
